// The "New design" flag: is the Lesha Bank design (Compliance V2) on for this browser? Every V2 check reads it
// (`datoxV2Design()` in datox/api.ts delegates here), so the flag-off app is exactly Bobur's.
//
// - On by default in agent mode (leshabank: the bank's app is the new design, as its staged integration made it). A build
//   sets VITE_DATOX_V2_DESIGN=false to default to Bobur's classic app instead.
// - `?design=classic` / `?design=new` overrides it at runtime and is remembered in localStorage, so going back needs no
//   rebuild. A remembered choice wins over the build default on later loads.
// - Agent mode is required either way: without it the app is the plain opencode UI and there is no V2 to show.
// - The value is read once per page load; switching writes the choice and reloads, so one load never mixes designs.

export type DesignChoice = "new" | "classic"

export const DESIGN_STORAGE_KEY = "datox.design"
export const DESIGN_QUERY_KEY = "design"

export function designChoice(value: string | null | undefined): DesignChoice | undefined {
  const text = value?.trim().toLowerCase()
  return text === "new" || text === "classic" ? text : undefined
}

export type DesignInput = {
  agentMode: boolean
  // The build default: on unless VITE_DATOX_V2_DESIGN === "false"
  env: boolean
  // `?design=` on this load
  query?: DesignChoice
  // the remembered choice
  stored?: DesignChoice
}

export function resolveDesign(input: DesignInput) {
  if (!input.agentMode) return false
  const choice = input.query ?? input.stored
  if (choice) return choice === "new"
  return input.env
}

// The "New design" switch in the account menu: always there by default (the way back to classic). A build that
// defaults to classic shows it only once a `?design=` link has been opened.
export function designToggleVisible(input: DesignInput) {
  return input.agentMode && (input.env || !!(input.query ?? input.stored))
}

type DesignStorage = { get(): string | null; set(value: string): void }

const browserStorage: DesignStorage = {
  get: () => window.localStorage.getItem(DESIGN_STORAGE_KEY),
  set: (value) => window.localStorage.setItem(DESIGN_STORAGE_KEY, value),
}

function readStored(storage: DesignStorage): DesignChoice | undefined {
  try {
    return designChoice(storage.get())
  } catch {
    return undefined
  }
}

function writeStored(storage: DesignStorage, choice: DesignChoice) {
  try {
    storage.set(choice)
  } catch {
    // Private mode or blocked storage: the choice lasts only while ?design= is in the URL.
  }
}

// The build's default: the new design unless the build turns it off.
function buildDefault(env: Record<string, string | boolean | undefined>) {
  return env.VITE_DATOX_V2_DESIGN !== "false" && env.VITE_DATOX_V2_DESIGN !== false
}

// One load's input: the build flags, `?design=` from `search`, and the remembered choice. A link's choice is
// remembered for later loads without it (in-app links drop the query).
export function readDesignInput(source: { env: Record<string, string | boolean | undefined>; search: string; storage: DesignStorage }): DesignInput {
  const agentMode = source.env.VITE_DATOX_AGENT_MODE === "true"
  let query: DesignChoice | undefined
  try {
    query = designChoice(new URLSearchParams(source.search).get(DESIGN_QUERY_KEY))
  } catch {
    query = undefined
  }
  if (agentMode && query && query !== readStored(source.storage)) writeStored(source.storage, query)
  return { agentMode, env: buildDefault(source.env), query, stored: readStored(source.storage) }
}

let cached: { input: DesignInput; on: boolean } | undefined

function current() {
  if (!cached) {
    const env = import.meta.env as Record<string, string | boolean | undefined>
    const input = typeof window === "undefined"
      ? { agentMode: env.VITE_DATOX_AGENT_MODE === "true", env: buildDefault(env) }
      : readDesignInput({ env, search: window.location.search, storage: browserStorage })
    cached = { input, on: resolveDesign(input) }
  }
  return cached
}

export function v2DesignOn() {
  return current().on
}

export function v2DesignToggleVisible() {
  return designToggleVisible(current().input)
}

// The URL to load for a choice: the same page without `?design=` (it would override the remembered choice).
export function designReloadHref(href: string) {
  const url = new URL(href)
  url.searchParams.delete(DESIGN_QUERY_KEY)
  return url.toString()
}

// The account menu's switch: remember the choice, then reload so the whole app comes up in that design.
export function switchDesign(on: boolean) {
  writeStored(browserStorage, on ? "new" : "classic")
  window.location.replace(designReloadHref(window.location.href))
}

// Tests only: forget the value read for this load, or set it.
export function resetDesignForTest(on?: boolean) {
  cached = on === undefined ? undefined : { input: { agentMode: true, env: on }, on }
}
